import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col } from '../../themes/MoGrid';
import Info from './Info/Info';
import Body from './Body/Body';
import RelatedStories from './RelatedStories/RelatedStories';
import Gallery from './Gallery/Gallery';
import { Helmet } from "react-helmet";
import { getImage, getEnvironment } from 'components/utils/helper';
import { StoriesContainer, Share } from './Article.style';
import ShareSocials from 'components/common/ShareSocials/ShareSocials';
import MoDialog from 'components/common/Dialog/Dialog';
import ConfigContext from 'context/ConfigContext';

const Article = ({cms, components}) => {
    let config = useContext(ConfigContext);
    let country = config.getCountry();
    let language = config.getLanguage();
    const [openShare, setOpenShare] = useState(false);
    const {id, gallery, publishedDate, author, title, url, content, quote, quotePosition, wordCount, tags, description, metadata, locales} = components;
    const currentTag = (new URLSearchParams(window.location.search).get('tag')) ?
        new URLSearchParams(window.location.search).get('tag') : ((tags && tags.length > 0) ? tags[0].name : '');
    const [meta, setMeta] = useState({
      title: title,
      description: description,
      ogDescription: description,
      ogTitle: title,
      ogImage: gallery.length > 0 ? getImage(gallery[0], null, true) : null
    });
    
    useEffect(() => {
      //Set fallbacks based on metadata
      if(metadata){
        let newMeta = {
          title: metadata.metaTitle ? metadata.metaTitle : title,
          description: metadata.metaDescription ? metadata.metaDescription : description,
          ogDescription: metadata.ogDescription ? metadata.ogDescription : meta.title,
          ogTitle: metadata.ogTitle ? metadata.ogTitle : meta.description,
          ogImage: metadata ? getImage(metadata.ogImage, null, true): gallery.length > 0 && (
            <meta property="og:image" content={getImage(gallery[0], null, true)} />
          )
        }
        setMeta(newMeta);
      }
    },[]);
  
    function getAllStories() {
        let rawStories = (cms && cms.pages && cms.pages.stories && cms.pages.stories.components) ? cms.pages.stories.components : [];
        // Remove current story
        rawStories = rawStories.filter(story => {
            return story.components[0] !== id;
        });
        return rawStories.map(story => {
            let id = story.components[0];
            let item = cms.components[id];
            item.id = id;
            item.url = story.url;
            item.title = story.title;
            return item;
        });
    }
    
    function getRelatedStories(){
        let stories = [];
        let allStories = getAllStories();
        allStories.sort(function(a,b){
            if (a.publishedDate && b.publishedDate) {
                return new window.Date(b.publishedDate) - new window.Date(a.publishedDate);
            }
            return 0;
          });
        if(currentTag){
            stories = allStories.filter(story => {
                let includesTag = false;
                for(let i=0; i < story.tags.length; i++){
                    if((story.id !== id) && (story.tags[i].id === currentTag.toLowerCase())){
                        includesTag = true;
                        break;
                    }
                }
                return includesTag;
            }).slice(0,3);
        } else {
            stories = allStories.slice(0,3);
        }
        return stories;
    }

   function getCanonicalLinks (locales) {
   
     // Get href url bases on country and language
     let getHref = (country, language) => {
       let env = getEnvironment() === 'produs' ? 'movember.com' : 'uat.movember.com',
       urlLanguage = language === 'en' ? '' : `/${language}`;
       // Check if current url includes language
       let urlWithoutLanguage = (url.charAt(3) === '/') ? url.substring(3) : url;
       return `https://${country}.${env}${urlLanguage}${urlWithoutLanguage}`;
     };

     let bigSixCountries = ['au', 'us', 'uk', 'ca', 'nz', 'ie'],
     bigSixLocales = [];
     // Convert current locale to hreflang locale format in big 6 countries (ex: en_UK -> en-gb)
     if(locales){
      locales.forEach((locale) => {
        if (bigSixCountries.indexOf(locale.split("_")[1].toLowerCase()) >= 0) {
          bigSixLocales.push(
            locale.toLowerCase().replace("_", "-").replace("uk", "gb")
          );
        }
      });
     }

     return bigSixLocales.map((locale, i) => {
       let localeLanguage = locale.split('-')[0],
       localeCountry = locale.split('-')[1].replace("gb", "uk"); // Change country code gb to uk to compare with site country code
       let isCanonical = localeCountry === country && localeLanguage === language;
       return <link key={i} rel={isCanonical ? 'canonical' : 'alternate'} href={getHref(localeCountry,localeLanguage)} {...!isCanonical ? {hrefLang: locale} : ''} />  
     })
   }

    return (
      <Container>
        {/* React Helmet to manage meta tags in document head */}
        <Helmet>
          <meta name="title" content={`${meta.title} - Movember`} />
          <meta name="url" content={window.location.href}  />
          <meta name="description" content={meta.description}  />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={meta.ogTitle} />
          <meta property="og:description" content={meta.ogDescription} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={meta.ogImage} />
          {getCanonicalLinks(locales)}
        </Helmet>
        <StoriesContainer>
          <Gallery {...{ cms, gallery }} />
          <Row>
            <Col xs={12} sm={8}>
              <Info {...{ country, publishedDate, author, title }} />
              <Body {...{ cms, content, quote, quotePosition, wordCount, tags }} />
              <Share
                data-dl-category="content"
                data-dl-location="stories"
                data-dl-label="share page modal"
                onClick={() => setOpenShare(true)}
              >
                <i className="fas fa-share-alt" />
                {cms.snippets['text-common-share'].text}
              </Share>
              <MoDialog open={openShare} onClose={()=> setOpenShare(false)}>
                <ShareSocials
                  onSelect={()=> setOpenShare(false)}
                  location="story"
                  shareUrl={window.location.href} 
                  getSocialContext={() => {
                    return {
                      title: title,
                      desc: title,
                      url: window.location.href
                    }
                  }}
                  />
              </MoDialog>
            </Col>
            <Col xs={12} sm={4}>
              {getRelatedStories().length > 0 && (
                <RelatedStories
                  {...{cms, country, currentTag}}
                  stories={getRelatedStories()}
                />
              )}
            </Col>
          </Row>
        </StoriesContainer>
      </Container>
    );
}
 
export default Article;